import React, { memo } from "react";
import { MdClose } from "react-icons/md";
import HtmlRenderer from "./HtmlRenderer";
import { motion, AnimatePresence } from "framer-motion";

export interface LegalLink {
  id: string;
  label: string;
  url: string;
  content?: string;
}

export interface LegalLinkModalProps {
  selectedLink: LegalLink | null;
  isEditing: boolean;
  editingContent: string;
  onClose: () => void;
  onSave: (content: string) => void;
  onContentChange: (content: string) => void;
  onEditToggle?: (editing: boolean) => void;
}

/**
 * LegalLinkModal - Modal component for displaying legal link content in VIEW-ONLY mode
 * Content can only be edited through the SectionSettingsModal
 */
const LegalLinkModal: React.FC<LegalLinkModalProps> = ({
  selectedLink,
  onClose
}) => {
  if (!selectedLink) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900">
              {selectedLink.label}
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <MdClose className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Modal Content - VIEW ONLY */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            <div className="prose max-w-none">
              <HtmlRenderer 
                html={selectedLink.content || ""}
                className="text-gray-700 leading-relaxed"
              />
            </div>
          </div>

          {/* Modal Footer - Close button only */}
          <div className="flex justify-end p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default memo(LegalLinkModal);
